// File: src/components/admin/OAuthCallbackView.js
// OAuth return page for admin-initiated platform connects.
// Mounts at /integrations/oauth-callback/:platformId — the provider redirects
// the browser here with ?code&state (or ?error). We exchange the code via the
// admin worker, then return to /geo.

import React, { useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { Box, CircularProgress, Alert, Typography, Button, Stack } from '@mui/material';
import { adminApi, handleAdminApiError } from '../../services/adminApi';

const PLATFORM_LABELS = { 'microsoft-ads': 'Microsoft Advertising', 'google-ads': 'Google Ads' };

export default function OAuthCallbackView() {
    const { platformId } = useParams();
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const [state, setState] = useState({ phase: 'working' }); // working | success | error
    const ran = useRef(false);

    useEffect(() => {
        if (ran.current) return; // exchange exactly once (React 18 StrictMode double-invoke guard)
        ran.current = true;

        const code = params.get('code');
        const oauthState = params.get('state');
        const error = params.get('error');

        if (error) { setState({ phase: 'error', message: `Provider returned: ${error}` }); return; }
        if (!code || !oauthState) { setState({ phase: 'error', message: 'Missing code or state in callback.' }); return; }

        (async () => {
            try {
                await adminApi.completeIntegrationOAuth(platformId, { code, state: oauthState });
                setState({ phase: 'success' });
                setTimeout(() => navigate('/geo', { replace: true }), 1200);
            } catch (e) {
                setState({ phase: 'error', message: handleAdminApiError(e).message });
            }
        })();
    }, [platformId, params, navigate]);

    const label = PLATFORM_LABELS[platformId] || platformId;

    return (
        <Box sx={{ p: 5, textAlign: 'center' }} data-testid="oauth-callback-view">
            {state.phase === 'working' && (
                <Stack alignItems="center" spacing={2} data-testid="oauth-callback-working">
                    <CircularProgress />
                    <Typography>Connecting {label}…</Typography>
                </Stack>
            )}
            {state.phase === 'success' && (
                <Alert severity="success" data-testid="oauth-callback-success" sx={{ display: 'inline-flex' }}>
                    {label} connected. Returning to Geo Targets…
                </Alert>
            )}
            {state.phase === 'error' && (
                <Stack alignItems="center" spacing={2}>
                    <Alert severity="error" data-testid="oauth-callback-error">{state.message}</Alert>
                    <Button variant="outlined" onClick={() => navigate('/geo', { replace: true })}>Back to Geo Targets</Button>
                </Stack>
            )}
        </Box>
    );
}
